import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useFavorites } from '../../hooks/useFavorites';
import { subscribeAuth, signInWithEmail, signOutUser, getUserRole, getUserProfile } from '../../services/auth';
import type { UserRole } from '../../services/auth';
import { useUnreadCount } from '../../hooks/useMessaging';
import { searchListings } from '../../services/listings';
import type { Listing } from '../../services/listings';
import { subscribeUserOrders } from '../../services/orders';
import { db, auth } from '../../lib/firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { openAIWidget } from '../../utils/aiWidget';
import { toUserMessage } from '../../utils/errors';
import { subscribeUnreadNotifications } from '../../services/notifications';
import { sendWelcomeEmail } from '../../services/email';

interface CampusHeaderProps {
  userName?: string;
}

interface LoginModalProps {
  showLoginModal: boolean;
  setShowLoginModal: (show: boolean) => void;
  loginEmail: string;
  setLoginEmail: (email: string) => void;
  loginPassword: string;
  setLoginPassword: (password: string) => void;
  showPwd: boolean;
  setShowPwd: (show: boolean) => void;
  loginError: string | null;
  loginLoading: boolean;
  handleLogin: (e: React.FormEvent) => void;
}

const LoginModal: React.FC<LoginModalProps> = ({
  showLoginModal,
  setShowLoginModal,
  loginEmail,
  setLoginEmail,
  loginPassword,
  setLoginPassword,
  showPwd,
  setShowPwd,
  loginError,
  loginLoading,
  handleLogin
}) => {
  const navigate = useNavigate();
  
  if (!showLoginModal) return null;
  
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="header-login-modal-title">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <button aria-label="Close" onClick={() => setShowLoginModal(false)} className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition">
          <i className="ri-close-line text-lg"></i>
        </button>
        <div className="bg-gradient-to-r from-teal-600 to-blue-600 px-6 py-5 text-white">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-white text-xl"></i>
            </div>
            <div>
              <h2 id="header-login-modal-title" className="text-xl font-semibold">Sign in</h2>
              <p className="text-white/80 text-sm">Access your marketplace features</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-6">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="your.email@university.edu"
                  autoComplete="username email"
                  inputMode="email"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
                <button 
                  type="button" 
                  onClick={() => setShowPwd(!showPwd)} 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700" 
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                >
                  <i className={`ri-${showPwd ? 'eye-off-line' : 'eye-line'} text-lg`}></i>
                </button>
              </div>
            </div>
            <AnimatePresence>
              {loginError && (
                <motion.div
                  key={loginError}
                  initial={{ opacity: 0, y: -8, x: 0 }}
                  animate={{ opacity: 1, y: 0, x: [0, -8, 8, -6, 6, -3, 3, 0] }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ x: { duration: 0.45 }, opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
                  className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5"
                >
                  <i className="ri-error-warning-fill text-red-500 text-base flex-shrink-0 mt-0.5"></i>
                  <span className="font-medium">{loginError}</span>
                </motion.div>
              )}
            </AnimatePresence>
            <button type="submit" disabled={loginLoading} className="w-full inline-flex items-center justify-center gap-2 bg-teal-600 text-white py-3 px-4 rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm disabled:opacity-60">
              {loginLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
          <div className="mt-2 text-right">
            <button onClick={() => { setShowLoginModal(false); navigate('/auth/forgot-password'); }} className="text-xs text-blue-600 hover:text-blue-700">
              Forgot password?
            </button>
          </div>
          <div className="mt-4 text-center">
            <button onClick={() => { setShowLoginModal(false); navigate('/auth/sign-up'); }} className="text-gray-700 hover:text-teal-700 font-medium cursor-pointer text-sm">
              Don't have an account? Sign up
            </button>
          </div>
          <div className="mt-2 text-center">
            <button onClick={() => setShowLoginModal(false)} className="text-gray-500 hover:text-gray-700 cursor-pointer text-xs">
              Continue browsing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CampusHeader: React.FC<CampusHeaderProps> = ({ userName: propUserName }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showPwd, setShowPwd] = useState(false);
  const [showSignUpDropdown, setShowSignUpDropdown] = useState(false);
  useEffect(() => {
    if (!showSignUpDropdown) return;
    const close = () => setShowSignUpDropdown(false);
    document.addEventListener('click', close, { capture: true, once: true });
    return () => document.removeEventListener('click', close, { capture: true });
  }, [showSignUpDropdown]);
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadCount } = useUnreadCount();
  const { getTotalItems } = useCart();
  const [ordersCount, setOrdersCount] = useState(0);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const isDashboardRoute = location.pathname.includes('/buyer-dashboard') || location.pathname.includes('/seller-dashboard');

  useEffect(() => {
    let unsub: any;
    const user = auth.currentUser;
    if (!user) { setOrdersCount(0); return; }
    try {
      if (userRole === 'seller') {
        const q = query(
          collection(db, 'orders'),
          where('sellerId', '==', user.uid),
          // Only count active orders
          where('status', 'not-in', ['completed', 'cancelled'])
        );
        unsub = onSnapshot(q, (snap) => setOrdersCount(snap.size));
      } else {
        // Subscribe to only active buyer orders
        unsub = subscribeUserOrders(user.uid, (orders) => {
          setOrdersCount(orders.length);
        }, true);
      }
    } catch {
      setOrdersCount(0);
    }
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [userRole, isLoggedIn]);

  // Subscribe to unread notifications count
  useEffect(() => {
    let unsub: any;
    setNotificationsCount(0);
    const user = auth.currentUser;
    if (!user) return () => undefined;
    try {
      unsub = subscribeUnreadNotifications(user.uid, (count) => setNotificationsCount(count));
    } catch {
      setNotificationsCount(0);
    }
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [isLoggedIn]);

  // Search suggestions state
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Listing[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  // Subscribe to Firebase auth
  useEffect(() => {
    const unsub = subscribeAuth(async (user) => {
      if (user) {
        setIsLoggedIn(true);
        const { role, status } = await getUserRole(user.uid);
        setUserRole(role);
        setUserStatus(status ?? null);
        const profile = await getUserProfile(user.uid);
        setIsVerified(Boolean(profile?.isVerified));
      } else {
        setIsLoggedIn(false);
        setUserRole(null);
        setUserStatus(null);
        setIsVerified(false);
      }
    });
    return () => unsub();
  }, []);

  // Debounced search
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q || q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setLoadingSuggestions(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchListings(q.toLowerCase());
        setSuggestions(res);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Search failed', err);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Open header login modal when `?login=1` is present and user not logged in
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const shouldOpen = params.get('login') === '1';
    if (shouldOpen && !isLoggedIn) {
      setShowLoginModal(true);
    }
  }, [location.search, isLoggedIn]);

  // Clean up `login` query param when modal closes to prevent re-open loops
  useEffect(() => {
    if (!showLoginModal) {
      const params = new URLSearchParams(location.search || '');
      if (params.get('login') === '1') {
        params.delete('login');
        const newSearch = params.toString();
        navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' }, { replace: true });
      }
    }
  }, [showLoginModal, location.pathname, location.search, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoginLoading(true);
    try {
      const user = await signInWithEmail(loginEmail.trim(), loginPassword.trim());
      try {
        const { role } = await getUserRole(user.uid);
        await sendWelcomeEmail(user.uid, user.email || loginEmail.trim(), user.displayName || undefined, role || 'buyer');
      } catch {}
      setShowLoginModal(false);
    } catch (err: any) {
      setLoginError(toUserMessage(err));
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 md:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 shadow-sm">
              <i className="ri-graduation-cap-fill text-white text-lg"></i>
            </div>
            <div className="leading-none">
              <span className="block text-xl font-extrabold tracking-tight text-gray-900">Camp<span className="text-teal-600">ket</span></span>
              <span className="hidden sm:block text-[10px] font-medium uppercase tracking-[0.18em] text-gray-400">Campus Market</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 ml-12">
            <Link
              to="/home"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/home') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/marketplace" 
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/marketplace') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              Marketplace
            </Link>
            <Link
              to="/hostels"
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5 ${
                isActive('/hostels') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              Hostels
              <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-600">Soon</span>
            </Link>
            {isLoggedIn && userRole !== 'seller' && (
              <Link
                to="/sell"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isActive('/sell') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                Sell Item
              </Link>
            )}
            {isLoggedIn && (
              <Link
                to="/messages"
                className={`relative text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isActive('/messages') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                Messages
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-3 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
            )}
            <Link
              to="/notifications"
              className={`relative text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/notifications') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              <i className="ri-notification-3-line"></i>
              {notificationsCount > 0 && (
                <span className="absolute -top-2 -right-3 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                  {notificationsCount > 99 ? '99+' : notificationsCount}
                </span>
              )}
            </Link>
          </nav>

          {/* Search Bar */}
          <div className="hidden lg:flex flex-1 min-w-0 max-w-xl xl:max-w-2xl 2xl:max-w-3xl mx-4 xl:mx-6">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Search for textbooks, electronics, furniture..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              />
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>

              {showSuggestions && (
                <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {loadingSuggestions ? (
                    <div className="px-4 py-3 text-sm text-gray-600">Searching...</div>
                  ) : suggestions.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-600">No matches</div>
                  ) : (
                    <ul className="max-h-64 overflow-auto">
                      {suggestions.map((item) => (
                        <li key={item.id}>
                          <button
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setShowSuggestions(false); setSearchQuery(''); navigate(`/product/${item.id}`); }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded bg-gray-200 overflow-hidden flex items-center justify-center">
                                {item.images?.[0] ? (
                                  /\.(mp4|webm|mov|m4v|mpeg|mpg|mkv)$/i.test(item.images[0]) ? (
                                    <video src={item.images[0]} className="w-10 h-10 object-cover" muted playsInline />
                                  ) : (
                                    <img src={item.images[0]} alt={item.title} className="w-10 h-10 object-cover" />
                                  )
                                ) : (
                                  <i className="ri-image-2-line text-gray-400"></i>
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{item.title}</div>
                                <div className="text-xs text-gray-600">₦{(item.price || 0).toLocaleString()}</div>
                              </div>
                            </div>
                            <i className="ri-arrow-right-line text-gray-400"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Cart icon visible only when items exist */}
            {getTotalItems() > 0 && (
              <Link
                to="/cart"
                className="relative inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                aria-label="Cart"
              >
                <i className="ri-shopping-cart-2-line text-base"></i>
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                  {getTotalItems() > 99 ? '99+' : getTotalItems()}
                </span>
              </Link>
            )}

            {isLoggedIn ? (
              <>
                {propUserName && !isDashboardRoute && (
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap flex items-center">
                    Hi {propUserName.split(' ')[0]}
                    {userRole === 'seller' && (
                      <span className={`${(isVerified || (userStatus && userStatus !== 'pending' && userStatus !== 'rejected')) ? 'ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700' : 'ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700'}`}> 
                        {(isVerified || (userStatus && userStatus !== 'pending' && userStatus !== 'rejected')) ? 'Verified' : 'Pending'}
                      </span>
                    )}
                  </span>
                )}

                {/* Orders link (always visible) */}
                <Link
                  to={userRole === 'seller' ? '/seller-orders' : '/orders'}
                  className="hidden md:inline-flex items-center space-x-2 px-2 py-1 md:px-3 md:py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors whitespace-nowrap cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(userRole === 'seller' ? '/seller-orders' : '/orders');
                  }}
                >
                  <i className="ri-file-list-2-line text-xs md:text-sm"></i>
                  <span className="text-xs md:text-sm font-medium">Orders</span>
                  {ordersCount > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center rounded-full bg-purple-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                      {ordersCount > 99 ? '99+' : ordersCount}
                    </span>
                  )}
                </Link>

                <Link
                  to="/wallet"
                  className="hidden md:inline-flex items-center space-x-2 px-2 py-1 md:px-3 md:py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-wallet-line text-xs md:text-sm"></i>
                  <span className="text-xs md:text-sm font-medium">Wallet</span>
                </Link>

                <Link
                  to="/referrals"
                  className="hidden md:inline-flex items-center space-x-2 px-2 py-1 md:px-3 md:py-2 bg-pink-50 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-gift-line text-xs md:text-sm"></i>
                  <span className="text-xs md:text-sm font-medium">Referrals</span>
                </Link>
                <Link
                  to={userRole === 'seller' ? '/seller-dashboard' : '/buyer-dashboard'}
                  className="hidden md:inline-flex items-center space-x-2 px-2 py-1 md:px-3 md:py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-user-line text-xs md:text-sm"></i>
                  <span className="text-xs md:text-sm font-medium">{userRole === 'seller' ? 'Dashboard' : 'Profile'}</span>
                </Link>
                <button
                  onClick={signOutUser}
                  className="hidden md:inline-flex items-center space-x-2 px-2 py-1 md:px-3 md:py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-logout-box-r-line text-xs md:text-sm"></i>
                  <span className="text-xs md:text-sm font-medium">Logout</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="hidden md:inline-flex items-center space-x-2 px-2 py-1 md:px-3 md:py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-user-line text-xs md:text-sm"></i>
                  <span className="text-xs md:text-sm font-medium">Sign In</span>
                </button>
                <div className="hidden md:block relative">
                  <button
                    onClick={() => setShowSignUpDropdown(prev => !prev)}
                    className="inline-flex items-center space-x-2 px-2 py-1 md:px-3 md:py-2 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-user-add-line text-xs md:text-sm"></i>
                    <span className="text-xs md:text-sm font-medium">Sign Up</span>
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </button>
                  {showSignUpDropdown && (
                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden">
                      <Link
                        to="/auth/sign-up"
                        onClick={() => setShowSignUpDropdown(false)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-shopping-bag-line text-blue-600 text-sm"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Sign Up as Buyer</p>
                          <p className="text-xs text-gray-500">Browse & buy items</p>
                        </div>
                      </Link>
                      <div className="border-t border-gray-100"></div>
                      <Link
                        to="/sell"
                        onClick={() => setShowSignUpDropdown(false)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-store-2-line text-teal-600 text-sm"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Sign Up as Seller</p>
                          <p className="text-xs text-gray-500">List & sell your items</p>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Campus Guru moved to hamburger only (mobile menu below) */}

          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close-line' : 'menu-line'} text-lg`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col space-y-4">
              {/* Mobile Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search items..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                {showSuggestions && searchQuery.trim().length >= 2 && (
                  <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {loadingSuggestions ? (
                      <div className="px-4 py-3 text-sm text-gray-600">Searching...</div>
                    ) : suggestions.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-600">No matches</div>
                    ) : (
                      <ul className="max-h-64 overflow-auto">
                        {suggestions.map((item) => (
                          <li key={item.id}>
                            <button
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3"
                              onClick={() => { setShowSuggestions(false); setSearchQuery(''); setIsMobileMenuOpen(false); navigate(`/product/${item.id}`); }}
                            >
                              <div className="w-10 h-10 rounded bg-gray-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                                {item.images?.[0] ? (
                                  <img src={item.images[0]} alt={item.title} className="w-10 h-10 object-cover" />
                                ) : (
                                  <i className="ri-image-2-line text-gray-400"></i>
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{item.title}</div>
                                <div className="text-xs text-gray-600">₦{(item.price || 0).toLocaleString()}</div>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Mobile Navigation */}
              <nav className="flex flex-col space-y-2">
                <Link
                  to="/home"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    isActive('/home') ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/marketplace"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    isActive('/marketplace') ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Marketplace
                </Link>
                <Link
                  to="/hostels"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${
                    isActive('/hostels') ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Hostels
                  <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-600">Soon</span>
                </Link>
                {isLoggedIn && userRole !== 'seller' && (
                  <Link
                    to="/sell"
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      isActive('/sell') ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sell Item
                  </Link>
                )}
                {isLoggedIn && (
                  <Link
                    to="/messages"
                    className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      isActive('/messages') ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Messages
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-2 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>
                )}
                <Link
                  to="/notifications"
                  className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    isActive('/notifications') ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-notification-3-line"></i>
                  {notificationsCount > 0 && (
                    <span className="absolute -top-1 -right-2 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                      {notificationsCount > 99 ? '99+' : notificationsCount}
                    </span>
                  )}
                </Link>
                {isLoggedIn && (
                  <Link
                    to={userRole === 'seller' ? '/seller-orders' : '/orders'}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      isActive(userRole === 'seller' ? '/seller-orders' : '/orders') ? 'bg-purple-50 text-purple-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Orders
                    {ordersCount > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center rounded-full bg-purple-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                        {ordersCount > 99 ? '99+' : ordersCount}
                      </span>
                    )}
                  </Link>
                )}
                {getTotalItems() > 0 && (
                  <Link
                    to="/cart"
                    className="relative px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Cart
                    <span className="absolute -top-1 -right-2 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                      {getTotalItems() > 99 ? '99+' : getTotalItems()}
                    </span>
                  </Link>
                )}
                {isLoggedIn && (
                  <Link
                    to="/wallet"
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      isActive('/wallet') ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Wallet
                  </Link>
                )}
                {isLoggedIn && (
                  <Link
                    to={userRole === 'seller' ? '/seller-dashboard' : '/buyer-dashboard'}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      isActive(userRole === 'seller' ? '/seller-dashboard' : '/buyer-dashboard') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {userRole === 'seller' ? 'Dashboard' : 'Profile'}
                  </Link>
                )}
              </nav>

              {/* Mobile Auth Actions */}
              {!isLoggedIn && (
                <div className="flex flex-col space-y-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowLoginModal(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors cursor-pointer"
                  >
                    Sign In
                  </button>
                  <Link
                    to="/auth/sign-up"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                  >
                    <i className="ri-shopping-bag-line text-sm"></i>
                    Sign Up as Buyer
                  </Link>
                  <Link
                    to="/sell"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-100 transition-colors"
                  >
                    <i className="ri-store-2-line text-sm"></i>
                    Sign Up as Seller
                  </Link>
                </div>
              )}

              {/* Mobile Account Actions */}
              {isLoggedIn && (
                <div className="flex flex-col space-y-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      signOutUser();
                      setIsMobileMenuOpen(false);
                    }}
                    className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100"
                  >
                    Logout
                  </button>
                </div>
              )}

              {/* Mobile Referrals — only for signed-in users */}
              {isLoggedIn && (
                <Link
                  to="/referrals"
                  className="flex items-center space-x-2 px-3 py-2 bg-pink-50 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors whitespace-nowrap cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-gift-line text-sm"></i>
                  <span className="text-sm font-medium">Referrals</span>
                </Link>
              )}

              {isLoggedIn && (
                <>
                  {/* Mobile Get Support */}
                  <button
                    onClick={() => {
                      openAIWidget('I need help with a problem.');
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-lifebuoy-line text-sm"></i>
                    <span className="text-sm font-medium">Get Support</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {showLoginModal && (
        <LoginModal
          showLoginModal={showLoginModal}
          setShowLoginModal={setShowLoginModal}
          loginEmail={loginEmail}
          setLoginEmail={setLoginEmail}
          loginPassword={loginPassword}
          setLoginPassword={setLoginPassword}
          showPwd={showPwd}
          setShowPwd={setShowPwd}
          loginError={loginError}
          loginLoading={loginLoading}
          handleLogin={handleLogin}
        />
      )}
    </header>
  );
};

export default CampusHeader;